import moment from "moment";

import Constants from "helpers/constants";
import { convertDateToLocalDeadlineMoment } from "./timezones";

export const buildExportFilename = (type: string, name?: string) =>
  `${name || "download"}--${moment().format(
    Constants.DATE_FORMAT__EXPORT_FILE__YYYY_MM_DD__HH_mm
  )}.${type}`;

export const allFieldsEmpty = (fields: object) => {
  // @ts-ignore
  return Object.keys(fields).every((field) => fields[field] === "");
};

export const anyFieldsEmpty = (fields: object) => {
  // @ts-ignore
  return !Object.keys(fields).every((field) => fields[field] !== "");
};

export const navigateToUrl = (url: string) => {
  window.open(url.startsWith("http://") || url.startsWith("https://") ? url : `http://${url}`);
};

export const MOBILE_WINDOW_WIDTH = 560;
export const DEFAULT_WINDOW_WIDTH = 900;

type CSVHeader =
  | "Upload Project File"
  | "Upload New Stores"
  | "Upload New Users"
  | "User to Store Mapping"
  | "User to Store Manage Mapping"
  | "Upload New Chains"
  | "USER ROLE CHAIN"
  | "PROJECT STORE MAPPING"
  | "PROJECT USER MAPPING"
  | "Default";

type ICSVHeaders = {
  [key in CSVHeader]: Array<{ label: string; key?: string }>;
};

export const getTemplateHeaders = (uploadLabel: string) => {
  const CSV_HEADERS: ICSVHeaders = {
    "Upload Project File": [
      { label: "Chain Name", key: "chainName" },
      { label: "Project Name", key: "projectName" },
      { label: "Project Type", key: "projectType" },
      { label: "Timezone", key: "timezone" },
      { label: "Project Deadline", key: "projectDeadline" },
      { label: "Season", key: "season" },
      { label: "Project Year", key: "year" },
    ],
    "Upload New Stores": [
      { label: "Chain Name" },
      { label: "Retail Store Number" },
      { label: "Store Name" },
      { label: "Address Line 1" },
      { label: "Address Line 2" },
      { label: "City" },
      { label: "State" },
      { label: "Country" },
      { label: "Zip Code" },
      { label: "Cluster Store Group" },
      { label: "Ad Zone" },
      { label: "ABInBev Wholesaler" },
      { label: "Molson Coors Wholesaler" },
      { label: "Constellation Wholesaler" },
      { label: "Third Party Wholesaler" },
      { label: "Catalyst ID" },
    ],
    "Upload New Users": [
      { label: "Phone" },
      { label: "User Role" },
      { label: "Email Address" },
      { label: "First Name" },
      { label: "Last Name" },
      { label: "Company Representing" },
      { label: "Title" },
      { label: "Employer" },
      { label: "Region" },
      { label: "Wholesaler Numbers" },
      { label: "IRIS Access" },
      { label: "IRIS Email" },
    ],
    "User to Store Mapping": [{ label: "Retail Store Number" }, { label: "Email Address" }],
    "User to Store Manage Mapping": [
      { label: "Chain Name" },
      { label: "Retail Store Number" },
      { label: "Email Address" },
    ],
    "Upload New Chains": [
      { label: "Chain Name" },
      { label: "Channel" },
      { label: "Key Contact 1" },
      { label: "Key Contact 2" },
      { label: "Key Contact 3" },
      { label: "Feedback Limit" },
      { label: "Catalyst ID" },
      { label: "Chain Parent" },
    ],
    "USER ROLE CHAIN": [{ label: "Chain Name" }, { label: "Email Address" }, { label: "Role" }],
    "PROJECT STORE MAPPING": [
      { label: "File", key: "file" },
      { label: "Store Number", key: "storeNumber" },
      { label: "Store Deadline", key: "storeDeadline" },
    ],
    "PROJECT USER MAPPING": [
      { label: "Retail Store Number", key: "retailStoreNumber" },
      { label: "Email Address", key: "emailAddress" },
    ],
    Default: [{ label: "No template found." }],
  };

  // @ts-ignore
  return CSV_HEADERS[uploadLabel] || CSV_HEADERS["Default"];
};

export const isIe =
  !!navigator.userAgent &&
  (navigator.userAgent.toLowerCase().indexOf("msie") > -1 ||
    navigator.userAgent.toLowerCase().indexOf("trident") > -1);

export const getSteps = ({
  currentApproverRole,
  workInProgressBy,
  requestType,
}: {
  currentApproverRole: string;
  workInProgressBy: string;
  requestType: string;
}) => {
  const normalSteps = [
    { number: 1, title: "Open", displayValue: 1 },
    { number: 2, title: "Pending Review", displayValue: 2 },
    { number: 3, title: "Approved", displayValue: 3 },
    { number: 4, title: "Work in Process", displayValue: 4 },
    { number: 5, title: "Completed", displayValue: 5 },
  ].map((step) => {
    if (step.number === 2 && currentApproverRole)
      return { ...step, title: `Pending Review (${currentApproverRole})` };
    if (step.number === 4 && workInProgressBy)
      return { ...step, title: `Work in Process (${workInProgressBy})` };
    return step;
  });

  const threeStepSteps = [
    { number: 1, title: "Open", displayValue: 1 },
    { number: 4, title: "Work in Process", displayValue: 2 },
    { number: 5, title: "Completed", displayValue: 3 },
  ].map((step) =>
    step.number === 4 && workInProgressBy
      ? { ...step, title: `Work in Process (${workInProgressBy})` }
      : step
  );

  const THREE_STEP_REQUEST_TYPES = new Set([
    Constants.CHAIN_ACCESS_BY_EXTERNAL_PROJECT_ADMIN,
    Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_PROJECT_ADMIN,
  ]);

  return THREE_STEP_REQUEST_TYPES.has(requestType as Constants) ? threeStepSteps : normalSteps;
};

export const parseStringToJson = (data: any) => {
  try {
    return typeof data === "string" ? JSON.parse(data) : data;
  } catch {
    return {};
  }
};

export const getStepFromStatus = (status: number | string | undefined): number => {
  const STATUS_TO_STEP_MAP: Record<string, number> = {
    OPEN: 1,
    PENDING_REVIEW: 2,
    APPROVED: 3,
    WORK_IN_PROGRESS: 4,
    COMPLETED: 5,
    CANCELLED: 5,
  };
  return STATUS_TO_STEP_MAP[String(status ?? "").toUpperCase()] ?? 1;
};

export const getInitials = (name: string) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((n) => n[0].toUpperCase())
    .join(" "); // space-separated initials
};

export const getDisplayDate = ({
  date,
  timezone = "",
}: {
  date: string | number | Date;
  timezone?: string;
}) => {
  const dateOnly = moment.utc(date).format("YYYY-MM-DD");
  const displayDate = dateOnly ? `${moment(dateOnly).format("MMM DD, YYYY")} - ${timezone ?? "GMT"}` : "-";
  return displayDate;
};

export const getCalculatedStatus = (
  row: { store_validation_deadline: string; store_status: Constants },
  projectTimezone: string = "UTC"
): string => {
  if (
    !row.store_validation_deadline ||
    ![Constants.VALIDATION_IN_PROGRESS, Constants.VALIDATION_COMPLETE].includes(row.store_status)
  ) {
    return row.store_status;
  }

  const localDate = convertDateToLocalDeadlineMoment(
    row.store_validation_deadline,
    projectTimezone
  );

  if(!localDate){
    return row.store_status;
  }

  return localDate?.isSameOrBefore(moment())
    ? Constants.VALIDATION_COMPLETE
    : Constants.VALIDATION_IN_PROGRESS;

};
